import { ConfigData } from "./config";

/**
 * Geometry of the radar chart, driven by `N` = the number of segments declared
 * in the radar's `config.json`. The Tech radar keeps four segments, the AI
 * radar uses six, and both are drawn by the same code.
 *
 * Two angular frames are in play and must not be confused:
 *
 * - the **arc frame** used by `d3.arc` (and therefore by the ring arcs and the
 *   gradient clip paths): 0 rad is 12 o'clock and angles grow clockwise;
 * - the **maths frame** used by `BlipPoints` (`x = cos a`, `y = sin a`, with the
 *   y scale inverted so positive y points up): 0 rad is 3 o'clock and angles
 *   grow counter-clockwise on screen.
 *
 * They are related by `a_maths = pi/2 - a_arc`.
 */

export const segmentCount = (config: ConfigData) =>
  Object.keys(config.quadrants).length;

/**
 * Rotational index of a segment, clockwise from 12 o'clock.
 *
 * The four-segment radar has been published for years with its quadrants laid
 * out in *reading* order - 1 top-left, 2 top-right, 3 bottom-left, 4
 * bottom-right - which is not a rotational order and cannot be produced by any
 * sequential formula. This table is therefore kept verbatim: it is what
 * guarantees the Tech radar does not move by a single pixel. Any other N places
 * the segments sequentially clockwise from 12 o'clock.
 */
const LEGACY_FOUR_SEGMENT_ORDER = [3, 0, 2, 1];

export const segmentIndex = (position: number, n: number) =>
  n === 4 ? LEGACY_FOUR_SEGMENT_ORDER[position - 1] : position - 1;

/**
 * `[startAngle, endAngle]` of a segment in the arc frame, ready for `d3.arc`.
 *
 * For N = 4 the expressions are deliberately shaped so that they evaluate to
 * the exact same floating point values as the hard coded table they replace
 * (`[3pi/2, 4pi/2]`, `[0, pi/2]`, `[pi, 3pi/2]`, `[pi/2, pi]`).
 */
export const segmentArcAngles = (
  position: number,
  n: number
): [number, number] => {
  const index = segmentIndex(position, n);
  return [(2 * Math.PI * index) / n, (2 * Math.PI * (index + 1)) / n];
};

/** Angular width of one segment, in degrees. 90 when N = 4. */
export const segmentDegrees = (n: number) => 360 / n;

/**
 * Rotation to apply to an in-segment angle in the maths frame.
 *
 * Equivalent to `pi/2 - (index + 1) * 2pi/N` brought back into `[0, 2pi)`, but
 * written as a single division so that N = 4 reproduces the previous
 * `(pi * [1, 4, 2, 3][position - 1]) / 2` bit for bit.
 */
export const segmentShift = (position: number, n: number) => {
  const index = segmentIndex(position, n);
  return (Math.PI * (4 * (n - index - 1) + n)) / (2 * n);
};

/** Direction of the segment's bisector on screen (x right, y down). */
export const segmentBisector = (position: number, n: number) => {
  const [startAngle, endAngle] = segmentArcAngles(position, n);
  const mid = (startAngle + endAngle) / 2;
  return { x: Math.sin(mid), y: -Math.cos(mid) };
};
